import { ManageForm } from '@components/forms/ManageForm';
import { useQuery } from '@hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { SystemEffects } from './SystemEffects';
import { Query } from '@features/redux/apis/utils';
import { Box, Chip, Grid, Icon, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { HighlightOff } from '@mui/icons-material';
import { useSystemMutation, useSystemByIdQuery } from '@features/redux/apis/system';
import { SYSTEM_STATUS_OPTIONS, SystemModel, SystemModelMap, SystemStatus } from '@consts/systems';
import { FormActions } from '@components/forms/FormActions';
import { useSelector } from 'react-redux';

import { OrgDropDowns } from '@components/forms/dropdowns/OrgDropdowns';
import { Validate } from '@utils/validate';
import { ViewSystemLocation } from './ViewSystemLocation';
import { useLocationMapQuery, useNameMapQuery } from '@features/redux/apis/org';
import { FormSelect } from '@components/forms/dropdowns/base/FormSelect';
import { FormDisabledText } from '@components/forms/text/base/FormDisabledText';
import { LastConnected } from '@components/LastConnected';
import { ForceOffset } from './ForceOffset';

import type { RootState } from '@features/redux/store';
import { FormTextArea } from '@components/forms/text/base/FormTextArea';
import type { System as SystemType } from '@api/system';
import { Operation } from '@/types/general';
import { BillingType, UI_SELECTION_MAP, UISelection, WARRANTY_PLAN_MAP, WarrantyType } from '@consts/company';
import { BillingPlan } from './BillingPlan/BillingPlan';
import { AuditTimeline } from '@components/AuditTrail/AuditTimeline';
import dayjs from 'dayjs';
import { requestPlanChange, requestStationMove, cancelStationMove } from '@api/system';
import { useAlert } from '@hooks/useAlert';
import { useState } from 'react';
import { StationMoveDialog } from './StationMoveDialog';

// ? Business rules
// ? 1. Finance user -> full permissions, can change billing plan and warranty plan
// ? 2. Admin user -> full permissions, can change billing plan and warranty plan
// ? 3. Customer -> Can change billing plan from Prepaid to Cut Fee, but not the other way around

// ? Don't use withDraft unless we need to. We don't need to
// ? create a draft in the SystemEffects component, for example.
const useSystem = Query.withDraft(
    useSystemByIdQuery,
    {
        StationID: null,
        LocationID: null,
        CompanyID: null,
        ResellerID: null,
        Model: null,
        Hostname: '',
        CreatedDate: '',
        SerialNum: null,
        Status: SystemStatus.Active,
        LastConnected: null,
        ConfigHash: null,
        ProductID: null,
        Notes: '',
        CutPlanID: BillingType.CutFee,
        WarrantyPlanID: WarrantyType.Warranty,
        UISelectionID: UISelection.Standard,
    },
    (fetched, init) => {
        const next = structuredClone(fetched);
        for (const k in init) {
            const key = k as keyof SystemType;
            if (next[key] === undefined || next[key] === null) {
                (next[key] as any) = init[key];
            }
        }

        return next;
    }
);

export const System = ({ operation, onSuccess }: { operation: Operation; onSuccess: () => void }) => {
    const stationId = useQuery().get('id')!;
    const auditPk = `Station#${stationId}`;

    const role = useSelector((state: RootState) => state.auth!.Roles.role);

    const { data: nameMap, isLoading: nameMapLoading } = useNameMapQuery({});
    const { isLoading: locationMapLoading } = useLocationMapQuery({});

    const { data: system, isLoading: systemQueryLoading, draft, refetch } = useSystem({ id: stationId! });
    const [draftState, setDraftState] = draft;

    const [mutate, { isLoading: mutationLoading }] = useSystemMutation({
        fixedCacheKey: 'SystemMutation',
    });

    // Pending requests captured locally and executed on Update
    const [pendingPlanChange, setPendingPlanChange] = useState<{
        targetPlanId: BillingType;
        effectiveDate: string;
        justification?: string;
    } | null>(null);

    const [pendingStationMove, setPendingStationMove] = useState<{
        locationId: number;
        effectiveDate: string;
    } | null>(null);
    const [isMoveDialogOpen, setMoveDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const { IdToken } = useSelector((state: RootState) => state.auth);
    const { setAlert } = useAlert();
    const [cancelMoveLoading, setCancelMoveLoading] = useState(false);

    const isUnmigrated = !system?.SerialNum;

    const isReassignment =
        system?.LocationID !== draftState?.LocationID ||
        system?.CompanyID !== draftState?.CompanyID ||
        system?.ResellerID !== draftState?.ResellerID;

    const systemDataLoading = systemQueryLoading || mutationLoading;
    const orgDataLoading = nameMapLoading || locationMapLoading;
    const isUnassigned = !draftState?.LocationID;

    return (
        <>
            <SystemEffects onSuccess={onSuccess} />

            <ManageForm title={'RapidCut System Information'} loading={orgDataLoading || systemDataLoading} size={12}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12} display='flex' flexDirection='column' gap={1}>
                    <Grid container spacing={1} flexDirection='row' gap={1} p={1}>
                        <LastConnected timestamp={system?.LastConnected} />
                    </Grid>

                    <Paper elevation={0} sx={{ width: '100%', padding: 1, border: '1px solid #e0e0e0' }}>
                        <Typography fontSize={'18px'} pt={1} pl={1} pb={1} fontWeight={1000} color={'black'}>
                            System Info
                        </Typography>

                        <Grid container display='flex' flexDirection='row' gap={1} p={1}>
                            <Grid item xs={12} sm={12} md={5.8} lg={5.8} xl={5.8}>
                                <FormDisabledText
                                    size={12}
                                    label='System ID'
                                    value={system?.StationID?.toString() || 'N/A'}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.8} lg={5.8} xl={5.8}>
                                <FormDisabledText size={12} label='Serial Number' value={system?.SerialNum || 'N/A'} />
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.8} lg={5.8} xl={5.8}>
                                <FormDisabledText
                                    size={12}
                                    label='Model'
                                    value={SystemModelMap[system?.Model as any] || 'N/A'}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.8} lg={5.8} xl={5.8}>
                                <FormSelect
                                    label='Status'
                                    disabled={operation === Operation.View}
                                    size={12}
                                    draft={draft}
                                    stateKey='Status'
                                    options={SYSTEM_STATUS_OPTIONS}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.8} lg={5.8} xl={5.8}>
                                <BillingPlan
                                    disabled={!role.stores.edit}
                                    refetch={refetch}
                                    system={system!}
                                    draft={draft}
                                    pendingPlanChange={pendingPlanChange}
                                    onCancelPending={() => setPendingPlanChange(null)}
                                    onSchedulePlanChange={(state) => setPendingPlanChange(state)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={5.8} lg={5.8} xl={5.8}>
                                <FormSelect
                                    label='Warranty Plan'
                                    disabled={!role.prices.edit}
                                    size={12}
                                    draft={draft}
                                    stateKey='WarrantyPlanID'
                                    options={Object.entries(WARRANTY_PLAN_MAP).map(([key, value]) => ({
                                        label: value,
                                        value: +key,
                                    }))}
                                />
                            </Grid>

                            {/** Only show this option as available for the mini for now */}
                            {system?.Model === SystemModel.RCMINI && (
                                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                    <FormSelect
                                        label='UI Selection'
                                        disabled={!role.stores.edit}
                                        size={12}
                                        draft={draft}
                                        stateKey='UISelectionID'
                                        options={Object.entries(UI_SELECTION_MAP).map(([key, value]) => ({
                                            label: value,
                                            value: +key,
                                        }))}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    <Paper elevation={0} sx={{ width: '100%', padding: 1, border: '1px solid #e0e0e0' }}>
                        <Typography fontSize={'18px'} pt={1} pl={1} pb={1} fontWeight={1000} color={'black'}>
                            System Assignment
                        </Typography>

                        <Grid item md={12} sm={12}>
                            <Typography fontWeight='bold' fontSize='12px' ml={1}>
                                *Note that unmigrated RC2/RC3 systems cannot be reassigned
                            </Typography>
                        </Grid>

                        <Grid container spacing={1} flexDirection='row' p={1}>
                            <OrgDropDowns
                                disabled={operation === Operation.View || Boolean(system?.stationMoveRequest)}
                                size={12}
                                draft={draft}
                                options={{
                                    companies: true,
                                    resellers: true,
                                    locations: true,
                                }}
                                onLocationChange={(_, value: any) => {
                                    const { id } = value.pop() ?? {};
                                    const nextLocationId = id !== undefined ? parseInt(id) : null;

                                    if (nextLocationId && nextLocationId !== system?.LocationID) {
                                        setPendingStationMove({
                                            locationId: nextLocationId,
                                            effectiveDate: dayjs().toDate().toISOString(),
                                        });
                                        setMoveDialogOpen(true);
                                    } else {
                                        setPendingStationMove(null);
                                        setMoveDialogOpen(false);
                                    }
                                }}
                            />

                            {(pendingStationMove || system?.stationMoveRequest) && (
                                <Grid item xs={12}>
                                    <Box display='flex' alignItems='center' gap={1} ml={1} mt={0.5}>
                                        <Typography variant='body2'>
                                            Scheduled move to{' '}
                                            {nameMap?.locations?.[
                                                (system?.stationMoveRequest?.LocationID ||
                                                    pendingStationMove?.locationId)!
                                            ] ||
                                                `Location ${(system?.stationMoveRequest?.LocationID ||
                                                    pendingStationMove?.locationId)!}`}{' '}
                                            on{' '}
                                            {dayjs(
                                                system?.stationMoveRequest?.EffectiveDate ||
                                                    pendingStationMove?.effectiveDate
                                            ).format('YYYY-MM-DD')}
                                        </Typography>
                                        {!system?.stationMoveRequest ? (
                                            <IconButton
                                                size='small'
                                                onClick={() => {
                                                    setPendingStationMove(null);
                                                    setMoveDialogOpen(false);
                                                    setDraftState({
                                                        ...draftState,
                                                        LocationID: (system?.LocationID ?? null) as number | null,
                                                    });
                                                }}>
                                                <HighlightOff fontSize='small' />
                                            </IconButton>
                                        ) : (
                                            <IconButton
                                                size='small'
                                                disabled={cancelMoveLoading}
                                                onClick={async () => {
                                                    const moveRequestId =
                                                        system?.stationMoveRequest?.StationMoveRequestID;
                                                    if (!moveRequestId || !IdToken) return;
                                                    setCancelMoveLoading(true);
                                                    const resp = await cancelStationMove(IdToken, moveRequestId).catch(
                                                        (err) => ({
                                                            success: false,
                                                            message: err?.response?.data?.message,
                                                        })
                                                    );
                                                    if (!resp.success) {
                                                        setAlert(
                                                            resp.message || 'Failed to cancel station move',
                                                            'error'
                                                        );
                                                    } else {
                                                        setAlert(
                                                            'Station move request cancelled successfully',
                                                            'success'
                                                        );
                                                        refetch();
                                                    }
                                                    setCancelMoveLoading(false);
                                                }}>
                                                <HighlightOff fontSize='small' />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Grid>
                            )}

                            {role.isAdmin && role.stores.edit && (
                                <FormTextArea
                                    helperText='Notes (Internal-only)'
                                    placeholder='Notes (Internal-only)'
                                    draft={draft}
                                    stateKey='Notes'
                                    rows={5}
                                />
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                <Grid
                    container
                    mt={1}
                    spacing={1}
                    pl={2}
                    flexDirection='row'
                    flex={1}
                    width={'100%'}
                    justifyContent={'space-between'}>
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={system?.SerialNum ? 5.8 : 12}
                        xl={system?.SerialNum ? 5.8 : 12}>
                        <Paper elevation={0} sx={{ width: '100%', padding: 1, border: '1px solid #e0e0e0' }}>
                            <Typography fontSize={'18px'} pt={1} pl={1} pb={1} fontWeight={1000} color={'black'}>
                                System Location
                            </Typography>

                            <ViewSystemLocation locationId={system?.LocationID} />
                        </Paper>
                    </Grid>

                    {role.isAdmin && (
                        <Grid item xs={12} sm={12} md={12} lg={5.8} xl={5.8}>
                            <ForceOffset
                                serialNumber={system?.SerialNum}
                                productId={system?.ProductID}
                                stationID={system?.StationID}
                            />
                        </Grid>
                    )}
                </Grid>

                {operation !== Operation.Create && role.isAdmin && (
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Paper elevation={0} sx={{ width: '100%', padding: 1, border: '1px solid #e0e0e0', mt: 2 }}>
                            <Typography fontSize={'18px'} pt={1} pl={1} pb={1} fontWeight={1000} color={'black'}>
                                Audit Log
                            </Typography>
                            <AuditTimeline entityPk={auditPk} />
                        </Paper>
                    </Grid>
                )}

                {role.stores.edit && (
                    <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                        <FormActions
                            loading={saving || mutationLoading}
                            onSave={() => {
                                const run = async () => {
                                    setSaving(true);
                                    // 1) Execute any pending requests FIRST so navigation (triggered by mutate success) happens last
                                    if (pendingPlanChange) {
                                        const resp = await requestPlanChange(
                                            IdToken!,
                                            system!.StationID!,
                                            pendingPlanChange.justification || '',
                                            pendingPlanChange.effectiveDate,
                                            pendingPlanChange.targetPlanId
                                        ).catch((err) => ({ success: false, message: err?.response?.data?.message }));

                                        if (!resp.success) throw new Error(resp.message);
                                    }

                                    if (pendingStationMove) {
                                        const resp = await requestStationMove(
                                            IdToken!,
                                            system!.StationID!,
                                            pendingStationMove.locationId,
                                            pendingStationMove.effectiveDate
                                        ).catch((err) => ({ success: false, message: err?.response?.data?.message }));

                                        if (!resp.success) throw new Error(resp.message);
                                    }

                                    // 2) Prepare system payload. If move is scheduled for future, keep original LocationID on save
                                    let systemToSave = { ...draftState } as SystemType;
                                    if (pendingStationMove) {
                                        const isImmediate =
                                            dayjs(pendingStationMove.effectiveDate).isSame(dayjs(), 'day') ||
                                            dayjs(pendingStationMove.effectiveDate).isBefore(dayjs(), 'day');
                                        if (!isImmediate) {
                                            systemToSave = {
                                                ...systemToSave,
                                                LocationID: system?.LocationID ?? null,
                                            } as SystemType;
                                        }
                                    }

                                    // 3) Perform the system update LAST so success effects fire after all ops
                                    await mutate({
                                        system: systemToSave,
                                    });

                                    // 4) Clear pending local state post-save
                                    if (pendingPlanChange) setPendingPlanChange(null);
                                    if (pendingStationMove) setPendingStationMove(null);
                                    setSaving(false);
                                };

                                run();
                            }}
                            tooltip={
                                isReassignment && isUnmigrated
                                    ? "Can't reassign unmigrated RC2/RC3 systems!"
                                    : !Validate.system(draftState)
                                    ? 'Please fill all necessary fields.'
                                    : ''
                            }
                            onCancel={() => onSuccess()}
                            disabled={
                                (isReassignment && isUnmigrated) ||
                                !Validate.system(draftState) ||
                                (isUnassigned && !role.prices.edit && !role.isAdmin) ||
                                saving
                            }
                            isUpdate={true}
                        />
                    </Grid>
                )}

                <StationMoveDialog
                    open={isMoveDialogOpen}
                    onClose={() => {
                        setMoveDialogOpen(false);
                    }}
                    onConfirm={({ effectiveDateIso }) => {
                        setPendingStationMove((prev) => (prev ? { ...prev, effectiveDate: effectiveDateIso } : prev));
                        setMoveDialogOpen(false);
                    }}
                    currentLocationLabel={undefined}
                    targetLocationLabel={undefined}
                />
            </ManageForm>
        </>
    );
};
